import { Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/Button/Button';
import ConfirmModal from '../../components/ConfirmModal';
import SaveModal from '../../components/InputModal';
import useAlert from '../../hooks/useAlert';

import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RootState } from '../../modules';
import { deleteSavedBoxAsync, loadSavedBoxesAsync } from '../../modules/editor';
import { loadSavedBoxes, updateSavedBox } from '../../modules/editor/service';

// 컴포넌트 이미지 imports
import cntBImg from '../../assets/images/components/cntB.png';
import ctaOnlyImg from '../../assets/images/components/CTAOnly.png';
import donationImg from '../../assets/images/components/donationA.png';
import footerImg from '../../assets/images/components/footer.png';
import headerImg from '../../assets/images/components/header.png';
import htmlcodeImg from '../../assets/images/components/htmlcode.png';
import imageOnlyImg from '../../assets/images/components/imageOnly.png';
import mapImg from '../../assets/images/components/map.png';
import multiBImg from '../../assets/images/components/multiB.png';
import partitionImg from '../../assets/images/components/partition.png';
import permalinkImg from '../../assets/images/components/permalink.png';
import shareImg from '../../assets/images/components/share.png';
import shopImg from '../../assets/images/components/shopA.png';
import snsImg from '../../assets/images/components/sns.png';
import spacerImg from '../../assets/images/components/spacer.png';
import textButtonImg from '../../assets/images/components/textButton.png';
import textOnlyImg from '../../assets/images/components/textOnly.png';
import videoImg from '../../assets/images/components/video.png';
import emptyBoxImg from '../../assets/images/icon/empty_box.png';

function SavedBoxes() {
	const Alert = useAlert();
	const dispatch = useDispatch();
	const savedBoxes = useSelector((state: RootState) => state.editor.savedBoxes);
	const savedBoxesLoading = useSelector((state: RootState) => state.editor.savedBoxesLoading);
	const savedBoxesMeta = useSelector((state: RootState) => state.editor.savedBoxesMeta);
	const renamingBoxes = useSelector((state: RootState) => state.editor.renamingBoxes);
	const deletingBoxes = useSelector((state: RootState) => state.editor.deletingBoxes);
	const isStarter = useSelector((state: RootState) => state.auth.service.isStarter);
	const contentPreset = useSelector((state: RootState) => state.auth.account.options.contentPreset);
	
	// 이름 변경 모달 관련 state
	const [showRenameModal, setShowRenameModal] = useState(false);
	const [currentRenameBoxId, setCurrentRenameBoxId] = useState<string>('');
	const [currentRenameBoxName, setCurrentRenameBoxName] = useState<string>('');
	
	// 삭제 확인 모달 관련 state
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [currentDeleteBoxId, setCurrentDeleteBoxId] = useState<string>('');
	const [currentDeleteBoxName, setCurrentDeleteBoxName] = useState<string>('');
	


	useEffect(() => {
		// savedBoxesMeta가 없을 때만 로드 (이미 한 번 로드했다면 meta 정보가 있음)
		if (!isStarter && savedBoxes.length === 0 && !savedBoxesLoading && !savedBoxesMeta) {
			dispatch(loadSavedBoxesAsync.request({ offset: 0, limit: 33, isLoadMore: false }));
		}
	}, [dispatch, isStarter, savedBoxes.length, savedBoxesLoading, savedBoxesMeta]);

	const onDragStart = (e: any) => {
		// 저장된 상자의 ID를 전달 (나중에 addSavedBox 액션에서 사용)
		e.dataTransfer.setData("text", `saved_box:${e.target.dataset.boxId}`);
	}

	const onRename = (boxId: string, currentName: string, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		handleRename(boxId, currentName);
	}

	const onDelete = (boxId: string, boxName: string, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		handleDelete(boxId, boxName);
	}

	const handleRename = async (boxId: string, currentName: string) => {

		// 개수 제한이 있는 경우에만 체크 (수정은 개수가 증가하지 않지만 서버 로직상 체크할 수도 있음)
		if (contentPreset?.maxCount) {
			let currentCount = savedBoxesMeta?.total || 0;
			
			// 상자 메타 정보가 없으면 먼저 로드 (최소한으로 로드)
			if (!savedBoxesMeta && !savedBoxesLoading) {
				try {
					const response = await loadSavedBoxes(0, 1);
					currentCount = response.total;
				} catch (error) {
					console.error('상자 메타 정보 로드 실패:', error);
				}
			}
			
			if (currentCount >= contentPreset.maxCount) {
				Alert.error(`상자는 ${contentPreset.maxCount}개까지 저장할 수 있습니다.`);
				return;
			}
		}

		setCurrentRenameBoxId(boxId);
		setCurrentRenameBoxName(currentName);
		setShowRenameModal(true);
		window.parent.postMessage({ msg: 'dialogShow' }, '*');
	}

	const handleDelete = (boxId: string, boxName: string) => {
		setCurrentDeleteBoxId(boxId);
		setCurrentDeleteBoxName(boxName);
		setShowDeleteModal(true);
		window.parent.postMessage({ msg: 'dialogShow' }, '*');
	}

	const handleRenameModalClose = () => {
		setShowRenameModal(false);
		setCurrentRenameBoxId('');
		setCurrentRenameBoxName('');
		window.parent.postMessage({ msg: 'dialogHide' }, '*');
	}

	const handleRenameConfirm = async (newName: string) => {
		if (currentRenameBoxId && newName.trim()) {
			// API 직접 호출로 Promise 기반 에러 처리 가능
			try {
				await updateSavedBox(currentRenameBoxId, newName.trim());
				Alert.log('상자를 수정했습니다.');
			} catch (error: any) {
				Alert.error('이런, 뭔가 문제가 있는 것 같습니다.');
				throw error;
			}	
			
			// 현재 로드된 개수만큼 다시 로드 (사용자가 본 개수 유지)
			const currentLimit = Math.max(savedBoxes.length, 33);
			dispatch(loadSavedBoxesAsync.request({ offset: 0, limit: currentLimit, isLoadMore: false }));
		}
	}

	const handleDeleteModalClose = () => {
		setShowDeleteModal(false);
		setCurrentDeleteBoxId('');
		setCurrentDeleteBoxName('');
		window.parent.postMessage({ msg: 'dialogHide' }, '*');
	}

	const handleDeleteConfirm = () => {
		if (currentDeleteBoxId) {
			dispatch(deleteSavedBoxAsync.request(currentDeleteBoxId));
		}
	}

	// 상자 썸네일 생성 (API의 type 필드 사용)
	const generateThumbnail = (type: string) => {
		// 상자 타입에 따른 이미지 매핑
		const typeImages = {
			base: textOnlyImg,
			textOnly: textOnlyImg,
			textButton: textButtonImg,
			image: imageOnlyImg,
			imageOnly: imageOnlyImg,
			header: headerImg,
			footer: footerImg,
			video: videoImg,
			map: mapImg,
			shop: shopImg,
			shopA: shopImg,
			sns: snsImg,
			partition: partitionImg,
			htmlcode: htmlcodeImg,
			donation: donationImg,
			permalink: permalinkImg,
			share: shareImg,
			spacer: spacerImg,
			multiB: multiBImg,
			cntB: cntBImg,
			CTAOnly: ctaOnlyImg,
			naverAd: imageOnlyImg,
			default: textOnlyImg
		};
		
		const imageSrc = typeImages[type] || typeImages.default;
		
		return (
			<img 
				src={imageSrc} 
				alt={`${type || 'default'} component`}
				className="saved-box-thumbnail-img"
			/>
		);
	}



	// 로딩 스켈레톤 컴포넌트
	const renderLoadingSkeleton = () => (
		<>
			{Array.from({ length: 33 }).map((_, index) => (
				<div key={index} className="add-content-item saved-box-item skeleton">
					<div className="handle saved-box-handle skeleton-handle">
						<div className="saved-box-thumbnail skeleton-thumbnail"></div>
						<div className="saved-box-info skeleton-info">
						</div>
					</div>
				</div>
			))}
		</>
	);

	const handleUpgrade = () => {
		window.parent.postMessage({ msg: 'openAccountPlanModal' }, '*');
	}

	const handleLoadMore = () => {
		if (savedBoxesMeta) {
			const nextOffset = savedBoxesMeta.offset + savedBoxesMeta.limit;
			dispatch(loadSavedBoxesAsync.request({ 
				offset: nextOffset, 
				limit: 33, 
				isLoadMore: true 
			}));
		}
	}

	return (
		<div className="editor-sidebar-add-content-wrapper">
			<div className="editor-sidebar-add-content">
				{isStarter ? (
					<div className="saved-boxes-disabled">
						<img src={emptyBoxImg} alt="empty-box" className="empty-box-img" />
						<p>상자를 저장하면 언제든 불러와 사용할 수 있습니다.</p>
						<p>내 상자는 스탠다드, 프로,<br/>
						엔터프라이즈 요금제에서 사용할 수 있습니다.</p>
						<p>지금 업그레이드하고 더 많은 기능을 사용해 보세요.</p>
						<button onClick={handleUpgrade}>업그레이드 하기</button>
					</div>
				) : savedBoxesLoading ? (
					<>
						{renderLoadingSkeleton()}
					</>
				) : savedBoxes.length === 0 ? (
					<div className="saved-boxes-empty">
						<img src={emptyBoxImg} alt="empty-box" className="empty-box-img" />
						<p>저장한 상자가 없습니다.</p>
						<p>상자를 저장하면 언제든 불러와 사용할 수 있습니다.</p>
						<a href="https://help.stibee.com/email/edit/save-edit-box" target="_blank">내 상자 사용 방법 보기</a>
					</div>
				) : (
					<>
						{savedBoxes.map((box, index) => (
							<div className="add-content-item saved-box-item" key={box.id}>
								<div
									data-box-id={box.id}
									draggable="true"
									onDragStart={onDragStart}
									className="handle saved-box-handle"
								>
									<div className="saved-box-thumbnail">
										{generateThumbnail(box.type)}
									</div>
									<div className="saved-box-info">
										<Tooltip 
											title={box.name}
											placement="bottom"
											overlayInnerStyle={{
												fontSize: '12px',
												whiteSpace: 'normal',
												maxWidth: '100px'
											}}
										>
											<div className="title">
												{box.name}
											</div>
										</Tooltip>
									</div>
								</div>
									<div className="saved-box-actions">
										<button
											className="saved-box-action-btn"
											onClick={(e) => onRename(box.id.toString(), box.name, e)}
											title="이름 변경"
											disabled={renamingBoxes[box.id.toString()]}
										>
											<FontAwesomeIcon icon={faPen} />
										</button>
																		<button
											className="saved-box-action-btn delete"
											onClick={(e) => onDelete(box.id.toString(), box.name, e)}
											title="삭제"
											disabled={deletingBoxes[box.id.toString()]}
										>
											<FontAwesomeIcon icon={faTrashCan} />
										</button>
								</div>
							</div>
						))}
						
						{/* 더보기 버튼 */}
						{savedBoxesMeta?.hasMore && (
							<div className="saved-boxes-load-more">
								<Button 
									size="medium"
									color="tertiary"
									onClick={handleLoadMore}
									disable={savedBoxesLoading ? 'disable' : ''}
								>
									{savedBoxesLoading ? '불러오는 중...' : '더보기'}
								</Button>
							</div>
						)}
					</>
				)}
			</div>

			{/* 이름 변경 모달 */}
			<SaveModal
				title='상자를 수정합니다'
				placeholder='새로운 이름을 입력하세요'
				show={showRenameModal}
				onClose={handleRenameModalClose}
				onSave={handleRenameConfirm}
				initialValue={currentRenameBoxName}
			/>

			{/* 삭제 확인 모달 */}
			<ConfirmModal
				show={showDeleteModal}
				onClose={handleDeleteModalClose}
				onConfirm={handleDeleteConfirm}
				title="정말 삭제하시겠습니까?"
				message="삭제한 상자는 복구할 수 없습니다."
				itemName={currentDeleteBoxName}
				cancelText="아니요"
				confirmText="예"
			/>
		</div>
	);
}

export default SavedBoxes;
